import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { LogOut, User, Search, CreditCard, RefreshCw, X, AlertTriangle } from 'lucide-react';
import { authService } from '../../services/authService';
import { NotificationPopup } from '../../components/NotificationPopup';
import { useLanguage } from '../../hooks/useLanguage';

interface HeaderProps {
    onCreditUpdate?: () => Promise<void>;
    creditBalance?: { standard: number; ai: number } | null;
    onSearch?: (searchTerm: string) => void;
}

export const Header: React.FC<HeaderProps> = ({
                                                  onCreditUpdate,
                                                  creditBalance,
                                                  onSearch
                                              }) => {
    const { t, isRTL, currentLanguage } = useLanguage();
    const navigate = useNavigate();
    const location = useLocation();
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [showNotifications, setShowNotifications] = useState(() => {
        return localStorage.getItem('showNotifications') !== 'false';
    });
    const [showBankWarning, setShowBankWarning] = useState(false);

    // Vérifier les paramètres IBAN/SWIFT au chargement
    useEffect(() => {
        const checkBankDetails = () => {
            const user = JSON.parse(localStorage.getItem('user') || '{}');
            const iban = user.iban || '';
            const swift = user.swift || '';
            setShowBankWarning(!iban.trim() || !swift.trim());
        };

        checkBankDetails();

        // Écouter les changements dans localStorage
        const handleStorageChange = (e: StorageEvent) => {
            if (e.key === 'garageIban' || e.key === 'garageSwift') {
                checkBankDetails();
            }
        };

        window.addEventListener('storage', handleStorageChange);

        // Vérifier périodiquement (au cas où les changements sont faits dans le même onglet)
        const interval = setInterval(checkBankDetails, 2000);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
            clearInterval(interval);
        };
    }, []);

    // Écouter les changements de la préférence de notification
    useEffect(() => {
        const handleStorageChange = (e: StorageEvent) => {
            if (e.key === 'showNotifications') {
                setShowNotifications(e.newValue !== 'false');
            }
        };

        const checkNotificationPreference = () => {
            const currentPref = localStorage.getItem('showNotifications');
            setShowNotifications(currentPref !== 'false');
        };

        const interval = setInterval(checkNotificationPreference, 1000);

        window.addEventListener('storage', handleStorageChange);
        return () => {
            window.removeEventListener('storage', handleStorageChange);
            clearInterval(interval);
        };
    }, []);

    // Déterminer le titre en fonction du chemin actuel
    const getPageTitle = () => {
        const path = location.pathname;
        if (path.includes('/pending')) return t('header.pageTitle.pending');
        if (path.includes('/urgent')) return t('header.pageTitle.urgent');
        if (path.includes('/in-progress')) return t('header.pageTitle.inProgress');
        if (path.includes('/completed')) return t('header.pageTitle.completed');
        if (path.includes('/messages')) return t('header.pageTitle.messages');
        if (path.includes('/reviews')) return t('header.pageTitle.reviews');
        if (path.includes('/analytics')) return t('header.pageTitle.analytics');
        if (path.includes('/settings')) return t('header.pageTitle.settings');
        return t('header.pageTitle.dashboard');
    };

    // Réinitialiser la recherche lors du changement de page
    useEffect(() => {
        setSearchTerm('');
        if (onSearch) {
            onSearch('');
        }
    }, [location.pathname]);

    const handleLogout = () => {
        if (window.confirm(t('header.profile.logoutConfirm'))) {
            authService.logout();
            navigate('/', { replace: true });
        }
    };

    const handleRefreshCredits = async () => {
        if (onCreditUpdate && !isRefreshing) {
            setIsRefreshing(true);
            try {
                await onCreditUpdate();
            } finally {
                setIsRefreshing(false);
            }
        }
    };

    const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setSearchTerm(value);
        if (onSearch) {
            onSearch(value);
        }
    };

    const clearSearch = () => {
        setSearchTerm('');
        if (onSearch) {
            onSearch('');
        }
    };

    const shouldShowSearch =
        location.pathname.includes('/pending') ||
        location.pathname.includes('/urgent') ||
        location.pathname.includes('/in-progress') ||
        location.pathname.includes('/completed');

    // Obtenir le format de date selon la langue
    const getFormattedDate = () => {
        const locale = t('header.date.locale');
        return new Date().toLocaleDateString(locale, {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const handleGoToSettings = () => {
        navigate('/dashboard/settings');
    };

    return (
        <header className={`bg-white border-b border-gray-200 ${isRTL ? 'rtl' : ''}`}>
            {/* Bannière d'avertissement IBAN/SWIFT */}
        {/*    {showBankWarning && (
                <div className="bg-amber-50 border-b border-amber-200 px-4 py-2">
                    <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
                        <div className={`flex items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
                            <AlertTriangle className={`h-5 w-5 text-amber-600 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                            <span className="text-sm text-amber-800">
                    {t('header.bankWarning.message')}
                  </span>
                        </div>
                        <button
                            onClick={handleGoToSettings}
                            className={`text-sm font-medium text-amber-700 hover:text-amber-900 underline ${isRTL ? 'mr-4' : 'ml-4'}`}
                        >
                            {t('header.bankWarning.action')}
                        </button>
                    </div>
                </div>
            )}*/}

            <div className="px-4 sm:px-6 lg:px-8">
                <div className="flex items-center justify-between h-16">
                    {/* Titre dynamique basé sur le chemin */}
                    <div className="flex items-center">
                        <h1 className="text-xl font-semibold text-gray-900">
                            {getPageTitle()}
                        </h1>
                    </div>

                    {/* Barre de recherche */}
                    {shouldShowSearch && (
                        <div className="flex-1 max-w-lg mx-4">
                            <div className="relative">
                                <div className={`absolute inset-y-0 ${isRTL ? 'right-0 pr-3' : 'left-0 pl-3'} flex items-center pointer-events-none`}>
                                    <Search className="h-5 w-5 text-gray-400" />
                                </div>
                                <input
                                    type="text"
                                    value={searchTerm}
                                    onChange={handleSearch}
                                    className={`block w-full ${isRTL ? 'pr-10 pl-10' : 'pl-10 pr-10'} py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm ${isRTL ? 'text-right' : 'text-left'}`}
                                    placeholder={t('header.search.placeholder')}
                                    dir={isRTL ? 'rtl' : 'ltr'}
                                />
                                {searchTerm && (
                                    <button
                                        onClick={clearSearch}
                                        className={`absolute inset-y-0 ${isRTL ? 'left-0 pl-3' : 'right-0 pr-3'} flex items-center`}
                                        title={t('header.search.clear')}
                                    >
                                        <X className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                                    </button>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Widget des crédits */}
                    {creditBalance && (
                        <div className={`hidden sm:flex items-center bg-gray-50 rounded-lg p-2 ${isRTL ? 'ml-4' : 'mr-4'}`}>
                            <div className={`flex items-center ${isRTL ? 'ml-3' : 'mr-3'}`}>
                                <CreditCard className={`h-4 w-4 text-indigo-500 ${isRTL ? 'ml-1' : 'mr-1'}`} />
                                <span className="text-xs font-medium text-gray-500">{t('header.credits.title')}</span>
                            </div>
                            <div className={`flex ${isRTL ? 'space-x-reverse' : ''} space-x-3`}>
                                <div className="flex items-center">
                                    <span className="text-sm font-bold text-blue-600">{creditBalance.standard}</span>
                                    <span className={`text-xs text-gray-500 ${isRTL ? 'mr-1' : 'ml-1'}`}>{t('header.credits.standard')}</span>
                                </div>
                                <div className="flex items-center">
                                    <span className="text-sm font-bold text-green-600">{creditBalance.ai}</span>
                                    <span className={`text-xs text-gray-500 ${isRTL ? 'mr-1' : 'ml-1'}`}>{t('header.credits.ai')}</span>
                                </div>
                                <button
                                    onClick={handleRefreshCredits}
                                    disabled={isRefreshing}
                                    className="p-1 text-gray-400 hover:text-gray-600 rounded-full"
                                    title={t('header.credits.refresh')}
                                >
                                    <RefreshCw className={`h-3 w-3 ${isRefreshing ? 'animate-spin' : ''}`} />
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Actions et profil */}
                    <div className={`flex items-center ${isRTL ? 'space-x-reverse' : ''} space-x-4`}>
                        {/* Notifications */}
                        {showNotifications && <NotificationPopup />}

                        {/* Séparateur */}
                        {showNotifications && <div className="h-6 w-px bg-gray-200" />}

                        {/* Bouton de déconnexion */}
                        <button
                            onClick={handleLogout}
                            className="p-2 text-gray-400 hover:text-gray-500"
                            title={t('header.profile.logout')}
                        >
                            <LogOut className="h-6 w-6" />
                        </button>
                    </div>
                </div>
            </div>

            {/* Barre de navigation secondaire */}
            <div className="px-4 sm:px-6 lg:px-8 py-2 bg-gray-50">
                <div className="flex justify-between">
          <span className="text-sm text-gray-500">
            {getFormattedDate()}
          </span>
                    {/* Affichage mobile des crédits */}
                    {creditBalance && (
                        <div className={`sm:hidden flex items-center ${isRTL ? 'space-x-reverse' : ''} space-x-2`}>
                            <div className="flex items-center">
                                <CreditCard className={`h-4 w-4 text-indigo-500 ${isRTL ? 'ml-1' : 'mr-1'}`} />
                                <span className={`text-xs font-medium ${isRTL ? 'ml-1' : 'mr-1'}`}>{t('header.credits.title')}:</span>
                                <span className="text-xs font-bold text-blue-600">{creditBalance.standard}</span>
                                <span className={`text-xs text-gray-500 ${isRTL ? 'ml-1' : 'mr-1'}`}>/</span>
                                <span className="text-xs font-bold text-green-600">{creditBalance.ai}</span>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </header>
    );
};